"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Landmark, Plus, Copy, MoreHorizontal, Search, ArrowRightLeft, AlertCircle, Users, Clock } from "lucide-react";
import { CreateAccountModal } from "@/components/modals/create-account-modal";
import { VirtualAccountDetailsModal } from "@/components/modals/virtual-account-details-modal";
import { cn } from "@/lib/utils";

export default function VirtualAccountsPage() {
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const handleOpenDetails = (acc: any) => {
    setSelectedAccount(acc);
    setIsDetailsOpen(true);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Virtual Accounts</h1>
          <p className="text-sm text-muted-foreground">Dedicated settlement accounts for reconciliation.</p>
        </div>
        <CreateAccountModal>
          <Button size="sm" className="bg-primary text-primary-foreground h-9 px-4 rounded-lg font-semibold shadow-sm">
            <Plus className="mr-2 h-4 w-4" /> Provision Account
          </Button>
        </CreateAccountModal>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { l: "Active Accounts", v: "1,248", d: "Currently operational", i: Landmark, c: "text-emerald-600 bg-emerald-50" },
          { l: "Dedicated", v: "842", d: "Permanent assignments", i: Users, c: "text-primary bg-primary/10" },
          { l: "One-Off", v: "406", d: "Transaction-based", i: Clock, c: "text-amber-600 bg-amber-50" },
          { l: "Monthly Throughput", v: "₦45.2M", d: "Processed this month", i: ArrowRightLeft, c: "text-blue-600 bg-blue-50" },
        ].map((stat) => (
          <Card key={stat.l} className="border shadow-sm">
            <div className="p-4 flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{stat.l}</p>
                <p className="text-xl font-bold tracking-tight">{stat.v}</p>
                <p className="text-[11px] text-muted-foreground">{stat.d}</p>
              </div>
              <div className={cn("h-7 w-7 rounded-lg flex items-center justify-center", stat.c)}>
                <stat.i className="h-3.5 w-3.5" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card className="border shadow-sm">
        <CardHeader className="py-4 px-5 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-muted/5">
          <div className="space-y-0.5">
            <CardTitle className="text-sm font-bold">Account Inventory</CardTitle>
            <CardDescription className="text-[10px]">Search and manage your accounts</CardDescription>
          </div>
          <div className="relative w-full max-w-xs group">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input 
              placeholder="Search..." 
              className="h-8 pl-8 text-xs rounded-md bg-background" 
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-muted/10">
              <TableRow className="hover:bg-transparent border-b">
                <TableHead className="px-5 h-10 text-[10px] font-bold uppercase tracking-wider">Account Details</TableHead>
                <TableHead className="h-10 text-[10px] font-bold uppercase tracking-wider">Type</TableHead>
                <TableHead className="h-10 text-[10px] font-bold uppercase tracking-wider">Provider</TableHead>
                <TableHead className="h-10 text-[10px] font-bold uppercase tracking-wider">Inflow</TableHead>
                <TableHead className="h-10 text-[10px] font-bold uppercase tracking-wider">Status</TableHead>
                <TableHead className="h-10 text-right px-5"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[
                { name: "John Doe Services", bank: "Wema Bank", acc: "0123456789", amount: "₦250k", status: "Active", type: "success", accType: "Dedicated" },
                { name: "Transaction INV-001", bank: "Providus", acc: "0987654321", amount: "₦1.2M", status: "Active", type: "success", accType: "One-Off" },
                { name: "Global Trade", bank: "Wema Bank", acc: "1122334455", amount: "₦0", status: "Pending", type: "warning", accType: "Dedicated" },
                { name: "Invoice Payment #45", bank: "Providus", acc: "5566778899", amount: "₦85k", status: "Inactive", type: "muted", accType: "One-Off" },
                { name: "Salary Disburse", bank: "Wema Bank", acc: "6677889900", amount: "₦4.5M", status: "Active", type: "success", accType: "Dedicated" },
              ].map((acc) => (
                <TableRow key={acc.acc} className="hover:bg-muted/10 border-b last:border-0 transition-all">
                  <TableCell className="px-5 py-3">
                    <div className="text-xs font-semibold">{acc.name}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="font-mono text-[9px] text-muted-foreground">{acc.acc}</span>
                      <Button variant="ghost" size="icon" className="h-4 w-4 text-muted-foreground hover:text-primary"><Copy className="h-2.5 w-2.5" /></Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn(
                      "text-[9px] h-5 px-2 font-bold uppercase border",
                      acc.accType === "Dedicated" ? "border-primary/30 text-primary bg-primary/5" : "border-amber-500/30 text-amber-600 bg-amber-50"
                    )}>
                      {acc.accType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-[10px] font-medium text-muted-foreground">{acc.bank}</span>
                  </TableCell>
                  <TableCell className="font-bold text-xs">{acc.amount}</TableCell>
                  <TableCell>
                    <Badge className={cn(
                      "text-[8px] h-4 px-1.5 font-bold uppercase border-none",
                      acc.type === "success" ? "bg-emerald-100 text-emerald-700" :
                      acc.type === "warning" ? "bg-amber-100 text-amber-700" :
                      "bg-muted text-muted-foreground"
                    )}>
                      {acc.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right px-5">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 rounded-md hover:bg-primary/5 transition-all">
                          <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40 text-xs">
                        <DropdownMenuItem className="cursor-pointer" onClick={() => handleOpenDetails(acc)}>View Details</DropdownMenuItem>
                        <DropdownMenuItem className="cursor-pointer">Copy NUBAN</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive focus:bg-destructive/10 cursor-pointer">Deactivate Account</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <VirtualAccountDetailsModal 
        open={isDetailsOpen} 
        onOpenChange={setIsDetailsOpen} 
        account={selectedAccount} 
      />
    </div>
  );
}
